import { useEffect, useState } from 'react'
import { imageUrl } from '../imageFile'
import { Glyph, glyphFor, toneFor } from './glyphs'

/**
 * An item's picture: its photo, or a drawing of what kind of thing it is.
 *
 * Never a letter in a box — "C" says less than the name beside it already
 * does. The stand-in is a glyph for the kind of part (a camera, a screw, a
 * board) on a tint stable for its category, so a shelf of items nobody has
 * photographed yet still reads as different things at a glance, and the
 * photo-less ones are obviously waiting for a photo rather than broken.
 *
 * `fit="cover"` for tiles and thumbnails, where a consistent crop is what makes
 * a grid read as a grid; `contain` for the item page, where the whole part has
 * to be visible to answer "is this the right one?".
 */
export function ItemImage({
  imageId,
  category,
  name,
  fit = 'cover',
  className = '',
}: {
  imageId: string | null
  category: string
  name: string
  fit?: 'cover' | 'contain'
  className?: string
}): JSX.Element {
  // A photo that fails to load falls back to the drawing, never the browser's
  // torn-page glyph — the dev scenarios wipe the blob store on every run.
  const [broken, setBroken] = useState(false)
  useEffect(() => setBroken(false), [imageId])

  if (imageId === null || broken) {
    return (
      <span className={`inv-art inv-art--t${toneFor(category, name)} ${className}`} aria-hidden="true">
        <Glyph name={glyphFor(category, name)} size={24} />
      </span>
    )
  }
  return (
    <img
      className={`inv-art inv-art--photo inv-art--${fit} ${className}`}
      src={imageUrl(imageId)}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  )
}

/** A square thumbnail for a row. */
export function ItemThumb({
  imageId,
  category,
  name,
  size = 44,
}: {
  imageId: string | null
  category: string
  name: string
  size?: number
}): JSX.Element {
  return (
    <span className="inv-thumb" style={{ width: size, height: size }}>
      <ItemImage imageId={imageId} category={category} name={name} />
    </span>
  )
}
